/**
 * Tabela de requisições — compartilhada por Compra e Separação (Bloco 5,
 * 12/09/2026). O que muda entre os dois módulos entra por prop:
 * - `vocab`: SITUACAO_LABEL/TONE (e ORIGEM_LABEL/TONE quando `comOrigem`);
 * - `campoItens`: `qtd_itens` (compras) ou `total_itens` (separação);
 * - `comOrigem`: coluna "Origem" (só compras tem origem);
 * - `comRevisao`: selo "Em revisão" ao lado do status (só separação).
 *
 * No celular (≤ 768px) a tabela vira lista de cards (auditoria mobile
 * 2026-09-24, S2): a tabela de 480px numa tela de 418 colapsava Cliente,
 * Projeto e Itens na mesma coluna e jogava Data para fora da tela. O card
 * mostra número + status, cliente em destaque, projeto e "N itens · data".
 */
import React from "react";

import Checkbox from "../../../../components/Checkbox";
import useMediaQuery from "../../../../hooks/useMediaQuery";
import { mq } from "../../../../styles/canonical/breakpoints";
import {
  CardItem, CardLista, CheckCell, Mono, Pill, Table, TableScroll, TapCheck,
} from "./styles";

function dataBR(iso) {
  return iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
}

function Status({ r, vocab, comRevisao }) {
  return (
    <>
      <Pill $tone={vocab.SITUACAO_TONE[r.situacao]}>
        {vocab.SITUACAO_LABEL[r.situacao] || r.situacao}
      </Pill>
      {/* Bloqueio visivel na LISTA: o almoxarife precisa saber
          antes de abrir qual requisicao nao da pra tocar agora. */}
      {comRevisao && r.em_revisao ? (
        <Pill $tone="warning" style={{ marginLeft: 6 }}>Em revisão</Pill>
      ) : null}
    </>
  );
}

function Cards({ requisicoes, selecionadas, onToggleSel, onAbrir, podeGerir, vocab, campoItens, comOrigem, comRevisao }) {
  return (
    <CardLista data-testid="requisicoes-cards">
      {requisicoes.map((r) => {
        const sel = selecionadas.has(r.id);
        const origem = comOrigem ? (vocab.ORIGEM_LABEL?.[r.origem] || r.origem_label || r.origem) : null;
        return (
          <CardItem
            key={r.id}
            $sel={sel}
            role="button"
            tabIndex={0}
            onClick={() => onAbrir(r.id)}
            onKeyDown={(e) => { if (e.key === "Enter") onAbrir(r.id); }}
          >
            <div className="linha">
              {podeGerir ? (
                <TapCheck onClick={(e) => { e.stopPropagation(); onToggleSel(r.id); }}>
                  <Checkbox checked={sel} readOnly aria-label={`Selecionar ${r.numero}`} />
                </TapCheck>
              ) : null}
              <Mono className="cresce" $size={13} style={{ color: "var(--ink-2)" }}>{r.numero}</Mono>
              <span className="fim"><Status r={r} vocab={vocab} comRevisao={comRevisao} /></span>
            </div>
            <div className="titulo" title={r.cliente_nome}>{r.cliente_nome || "Sem cliente"}</div>
            <div className="sub" title={r.projeto_resumo}>{r.projeto_resumo || "—"}</div>
            <div className="sub">
              {r[campoItens]} itens · {dataBR(r.created_at)}
              {origem ? ` · ${origem}` : ""}
              {r.atendida_em ? ` · atendida ${dataBR(r.atendida_em)}` : ""}
            </div>
          </CardItem>
        );
      })}
    </CardLista>
  );
}

export default function Tabela({
  requisicoes, selecionadas, onToggleSel, onAbrir, podeGerir,
  vocab, campoItens = "qtd_itens", comOrigem = false, comRevisao = false,
}) {
  const mobile = useMediaQuery(mq.mobile);
  if (mobile) {
    return (
      <Cards
        requisicoes={requisicoes}
        selecionadas={selecionadas}
        onToggleSel={onToggleSel}
        onAbrir={onAbrir}
        podeGerir={podeGerir}
        vocab={vocab}
        campoItens={campoItens}
        comOrigem={comOrigem}
        comRevisao={comRevisao}
      />
    );
  }
  return (
    <TableScroll>
      <Table>
        <colgroup>
          {podeGerir ? <col style={{ width: 40 }} /> : null}
          <col style={{ width: 130 }} />
          <col />
          <col />
          <col style={{ width: 70 }} />
          {comOrigem ? <col style={{ width: 140 }} /> : null}
          <col style={{ width: 130 }} />
          <col style={{ width: 110 }} />
        </colgroup>
        <thead>
          <tr>
            {podeGerir ? <th aria-label="Selecionar" /> : null}
            <th>Nº</th>
            <th>Cliente</th>
            <th>Projeto / ambientes</th>
            <th style={{ textAlign: "center" }}>Itens</th>
            {comOrigem ? <th>Origem</th> : null}
            <th>Status</th>
            <th>Data</th>
          </tr>
        </thead>
        <tbody>
          {requisicoes.map((r) => (
            <tr key={r.id} onClick={() => onAbrir(r.id)}>
              {podeGerir ? (
                <CheckCell onClick={(e) => { e.stopPropagation(); onToggleSel(r.id); }}>
                  <Checkbox
                    checked={selecionadas.has(r.id)}
                    readOnly
                    aria-label={`Selecionar ${r.numero}`}
                  />
                </CheckCell>
              ) : null}
              <td title={r.numero} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                {r.numero}
              </td>
              <td title={r.cliente_nome}>{r.cliente_nome || "—"}</td>
              <td title={r.projeto_resumo}>{r.projeto_resumo || "—"}</td>
              <td style={{ textAlign: "center" }}>{r[campoItens]}</td>
              {comOrigem ? (
                <td title={r.origem_label}>
                  <Pill $tone={vocab.ORIGEM_TONE?.[r.origem] || "neutral"}>
                    {vocab.ORIGEM_LABEL?.[r.origem] || r.origem_label || r.origem}
                  </Pill>
                </td>
              ) : null}
              <td><Status r={r} vocab={vocab} comRevisao={comRevisao} /></td>
              <td>
                {dataBR(r.created_at)}
                {/* Bloco 8: quando a requisicao fechou (o Cut sincroniza esse carimbo). */}
                {r.atendida_em ? <div style={{ fontSize: 11, color: "var(--ink-3)" }} title="Atendida em">atendida {dataBR(r.atendida_em)}</div> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
